import { useMemo, useState } from "react";
import type { UserRow } from "../sql";
import { fmtN, fmtPct } from "../../../lib/funnel/helpers";
import { downloadCsv } from "../../../lib/funnel/csv";
import type { Filters } from "../../../lib/funnel/filters";
import { SectionCardHeader } from "../../../components/ChartCard";
import type { FilterPillSpec } from "../../../components/FilterPills";

// Five action cards. Each defines a drop-off cohort + a one-line CTA so they
// read as Loops/Slack outreach triggers, not just facts. Clicking a card
// either filters the user table below it (via onFilterUsers) or expands a
// list inline; we ship the inline expansion + CSV export here.

type Cohort = {
  key: string;
  title: string;
  cta: string;
  description: string;
  predicate: (u: UserRow) => boolean;
};

const COHORTS: Cohort[] = [
  {
    key: "no_calendar",
    title: "Never connected calendar",
    cta: "Trigger calendar-onboarding nudge",
    description: "Active users (≥1 transcript or summary) who never connected a calendar. They're using the recorder but not the integration that flips meeting volume.",
    predicate: (u) => !u.calendar_connected && (u.transcript_count > 0 || u.synopsis_count > 0),
  },
  {
    key: "dormant",
    title: "Dormant or never activated (14+ days)",
    cta: "Re-engagement or onboarding nudge",
    description: "Signed up 14+ days ago and either never activated, or last activity (transcript or summary) is 14+ days old. No paid plan. Mix of users who need a re-engagement email and users who need an onboarding nudge.",
    predicate: (u) => {
      if (u.on_paid_plan) return false;
      if (u.days_since_signup < 14) return false;
      const last = u.last_active_date;
      if (!last) return true;
      const days = (Date.now() - new Date(last + "T00:00:00Z").getTime()) / 86400000;
      return Number.isFinite(days) && days >= 14;
    },
  },
  {
    key: "summaries_no_client_chat",
    title: "Summaries but no Ask (client)",
    cta: "Onboarding tour: Ask Marloo client",
    description: "Has summaries but never used Ask Marloo client mode. Ask Marloo client is the strongest pre-paid predictor; these users are leaving the highest-leverage feature on the table.",
    predicate: (u) => u.synopsis_count > 0 && u.chat_client_count === 0,
  },
  {
    key: "paid_no_client_chat",
    title: "Paid users without Ask (client)",
    cta: "Account success outreach",
    description: "Already paid but never used Ask Marloo client mode. Drives expansion conversations and product feedback.",
    predicate: (u) => u.on_paid_plan && u.chat_client_count === 0,
  },
  {
    key: "power_free",
    title: "Power free users (5+ summaries)",
    cta: "Sales touchpoint",
    description: "Free users with 5+ summaries. Highest-intent free cohort; manual sales touch is usually the right move here.",
    predicate: (u) => !u.on_paid_plan && u.synopsis_count >= 5,
  },
];

export function DropoffCohorts({ users, filters, pagePills }: { users: UserRow[]; filters: Filters; pagePills?: FilterPillSpec[] }) {
  const total = users.length;
  const data = useMemo(
    () => COHORTS.map((c) => ({ cohort: c, matches: users.filter(c.predicate) })),
    [users],
  );

  return (
    <section className="card">
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            Drop-off cohorts (action lists)
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
              textTransform: "uppercase", color: "#92400e",
              background: "#fef3c7", border: "1px solid #fde68a",
              borderRadius: 4, padding: "2px 6px",
            }}>
              Work in Progress
            </span>
          </span>
        }
        subtitle="Click a card to inspect or export the user list. Every card maps to a Loops or Slack outreach trigger."
        filterPills={pagePills}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
        {data.map(({ cohort, matches }, i) => {
          // Top row: 3 cards spanning 2 cols each. Bottom row: 2 cards spanning 3 cols each.
          const span = i < 3 ? 2 : 3;
          return (
            <div key={cohort.key} style={{ gridColumn: `span ${span}`, display: "flex" }}>
              <DropoffCard cohort={cohort} matches={matches} total={total} filters={filters} />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function DropoffCard({
  cohort, matches, total, filters,
}: {
  cohort: Cohort; matches: UserRow[]; total: number; filters: Filters;
}) {
  const [expanded, setExpanded] = useState(false);
  const exportCsv = () => {
    downloadCsv(
      matches.map((u) => ({
        user_external_id: u.user_external_id,
        company_external_id: u.company_external_id,
        email: u.email,
        full_name: u.full_name,
        country_code: u.country_code,
        billing_plan: u.billing_plan,
        had_demo: u.had_demo,
        days_since_signup: u.days_since_signup,
        last_active_date: u.last_active_date,
        transcript_count: u.transcript_count,
        synopsis_count: u.synopsis_count,
        chat_meeting_count: u.chat_meeting_count,
        chat_client_count: u.chat_client_count,
      } as Record<string, unknown>)),
      [
        "user_external_id", "company_external_id", "email", "full_name", "country_code",
        "billing_plan", "had_demo", "days_since_signup", "last_active_date",
        "transcript_count", "synopsis_count", "chat_meeting_count", "chat_client_count",
      ],
      `dropoff-${cohort.key}`,
      filters,
    );
  };
  return (
    <div style={{
      border: "1px solid var(--border-default)", borderRadius: 8, padding: 12,
      background: "var(--white)", display: "flex", flexDirection: "column", gap: 8,
      width: "100%", minHeight: "100%",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <strong style={{ fontSize: 13 }}>{cohort.title}</strong>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 18, fontWeight: 600, color: "var(--color-teal)" }}>{fmtN(matches.length)}</span>
      </div>
      <div style={{ fontSize: 11, color: "var(--fg-3)" }}>
        {fmtPct(matches.length, total)} of users in cohort
      </div>
      <div style={{ fontSize: 12, color: "var(--fg-1)", lineHeight: 1.45 }}>{cohort.description}</div>
      <div style={{ fontSize: 11, color: "var(--color-teal)", fontWeight: 600, marginTop: "auto" }}>→ {cohort.cta}</div>
      <div style={{ display: "flex", gap: 6 }}>
        <button type="button" className="btn btn-sm" onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Hide list" : "Preview list"}
        </button>
        <button type="button" className="btn btn-sm" onClick={exportCsv} disabled={matches.length === 0}>
          <i className="ti ti-download" /> CSV
        </button>
      </div>
      {expanded && (
        <div style={{ maxHeight: 360, overflow: "auto", border: "1px solid var(--border-default)", borderRadius: 4 }}>
          {matches.length === 0 ? (
            <div style={{ padding: 8, fontSize: 11, color: "var(--fg-3)" }}>No users match.</div>
          ) : (
            <table className="t" style={{ fontSize: 11, width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Email</th>
                  <th style={{ textAlign: "left" }}>Name</th>
                  <th style={{ textAlign: "left" }}>Company</th>
                  <th style={{ textAlign: "left" }}>Demo source</th>
                </tr>
              </thead>
              <tbody>
                {matches.slice(0, 100).map((u) => {
                  const demoer = u.had_demo
                    ? (u.first_demo_organiser_email
                        ?? u.company_first_demo_organiser_email
                        ?? "Demo (organiser unknown)")
                    : "Self sign-up";
                  return (
                    <tr key={`${u.user_external_id}-${u.company_external_id}`}>
                      <td>{u.email}</td>
                      <td>{u.full_name || ""}</td>
                      <td>{u.company_name ?? ""}</td>
                      <td style={{ color: u.had_demo ? "var(--color-teal)" : "var(--fg-3)" }}>{demoer}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {matches.length > 100 && (
            <div style={{ padding: 6, fontSize: 10, color: "var(--fg-3)", borderTop: "1px solid var(--border-default)" }}>
              Showing 100 of {matches.length}. Export CSV for the full list.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
